import React from 'react';
import { V2FormProvider } from '@abyss/web/ui/FormProvider';
import { Grid } from '@abyss/web/ui/Grid';
import { Box } from '@abyss/web/ui/Box';
import { Heading } from '@abyss/web/ui/Heading';
import { V2TextInput } from '@abyss/web/ui/TextInput';
import { V2SelectInput } from '@abyss/web/ui/SelectInput';
import { GridBuilder } from '@src/components/global/GridBuilder';
import { ResponseGridController } from './ResponseGrid.Controller';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import { GridDefaultColumns } from '@src/components/global/GridBuilder/interfaces/IGridBuilderProps';
import { Tooltip } from '@abyss/web/ui/Tooltip';
import { Button } from '@abyss/web/ui/Button';
import { SurveyResponseModal } from './SurveyResponseModal';

export const ResponseGrid = () => {
  const {
    form,
    gridProps,
    surveyAnswers,
    surveySourceSessionId,
    onResponseClick,
    isAdmin,
    isViewer,
    pageData,
  } = ResponseGridController();

  gridProps.columns = [
    ...(isAdmin || isViewer
      ? [
          {
            field: 'sourceSessionId',
            heading: '',
            width: 60,
            disableSort: true,
            disableResize: true,
            link: {
              url: '/responses/',
              showValue: false,
              appendValueInUrl: true,
              icon: (isAdmin ? 'edit' : 'preview') as any,
            },
          },
        ]
      : []),
    {
      field: 'number',
      heading: 'Number',
      width: 70,
      disableSort: true,
    },
    {
      field: 'responseText',
      heading: 'Response',
      width: 120,
      cell: ({ row }: any) => (
        <Tooltip content="View Survey Response" asChild={false}>
          <Button
            variant="ghost"
            size="$sm"
            css={{ padding: '0', minWidth: 'auto' }}
            onClick={() => onResponseClick(row.original.sourceSessionId)}
          >
            <IconSymbol
              icon="article"
              variant="outlined"
              color="$core.color.neutral.90"
            />
          </Button>
        </Tooltip>
      ),
    },
    { field: 'responderName', heading: 'Recipient Display Name', width: 180 },
    {
      field: 'recipientDepartment',
      heading: 'Recipient Department',
      width: 160,
    },
    { field: 'recipientDivision', heading: 'Recipient Division', width: 160 },
    { field: 'assignedToName', heading: 'Assigned To', width: 160 },
    { field: 'technologyName', heading: 'Technology', width: 150 },
    { field: 'themeName', heading: 'Theme', width: 150 },
    { field: 'actionName', heading: 'Action', width: 150 },
    { field: 'actionOwnerName', heading: 'Action Owner', width: 150 },
    { field: 'followUpStatusName', heading: 'Follow Up Status', width: 150 },
    { field: 'followUpMessage', heading: 'Follow Up Response', width: 150 },
    {
      field: 'followUpDate',
      heading: 'Follow Up Date',
      width: 150,
      isDate: true,
      dateFormat: 'MM/DD/YYYY',
    },
    { field: 'regionName', heading: 'Region', width: 150 },
    GridDefaultColumns.CreatedDate,
    GridDefaultColumns.CreatedBy,
    GridDefaultColumns.UpdatedDate,
    GridDefaultColumns.UpdatedBy,
  ];

  return (
    <Box color="transparent" padding="$md" width="100%">
      <V2FormProvider state={form}>
        <Grid>
          <Grid.Col span={{ xs: '100%' }}>
            <Grid>
              <Grid.Col span={{ xs: 12, sm: 12 }}>
                <Heading display="h4" color="$primary1">
                  Filters
                </Heading>
              </Grid.Col>

              <Grid.Col span={{ xs: 12, sm: 6, md: 3 }}>
                <V2TextInput
                  label="Recipient Display Name"
                  model="recipientDisplayName"
                  isClearable
                />
              </Grid.Col>
              <Grid.Col span={{ xs: 12, sm: 6, md: 3 }}>
                <V2TextInput
                  label="Recipient Department"
                  model="recipientDepartment"
                  isClearable
                />
              </Grid.Col>
              <Grid.Col span={{ xs: 12, sm: 6, md: 3 }}>
                <V2TextInput
                  label="Response"
                  model="responseText"
                  isClearable
                />
              </Grid.Col>
              <Grid.Col span={{ xs: 12, sm: 6, md: 3 }}>
                <V2TextInput
                  label="Assigned To"
                  model="assignedTo"
                  isClearable
                />
              </Grid.Col>
              <Grid.Col span={{ xs: 12, sm: 6, md: 3 }}>
                <V2TextInput
                  label="Technology"
                  model="technology"
                  isClearable
                />
              </Grid.Col>
              <Grid.Col span={{ xs: 12, sm: 6, md: 3 }}>
                <V2TextInput label="Theme" model="theme" isClearable />
              </Grid.Col>
              <Grid.Col span={{ xs: 12, sm: 6, md: 3 }}>
                <V2TextInput label="Action" model="action" isClearable />
              </Grid.Col>
              <Grid.Col span={{ xs: 12, sm: 6, md: 3 }}>
                <V2TextInput
                  label="Action Owner"
                  model="actionOwner"
                  isClearable
                />
              </Grid.Col>

              <Grid.Col span={{ xs: 12, sm: 6, md: 3 }}>
                <V2TextInput
                  label="Follow-Up Status"
                  model="followUpStatus"
                  isClearable
                />
              </Grid.Col>

              <Grid.Col span={{ xs: 12, sm: 6, md: 3 }}>
                <V2TextInput
                  label="Recipient Division"
                  model="recipientDivision"
                  isClearable
                />
              </Grid.Col>
              <Grid.Col span={{ xs: 12, sm: 6, md: 3 }}>
                <V2SelectInput
                  label="Region"
                  model="regionId"
                  options={pageData.regions}
                  isSearchable
                  isClearable
                  virtual
                  searchConfig={{ threshold: 0.3, ignoreLocation: true }}
                />
              </Grid.Col>
            </Grid>
          </Grid.Col>

          <Grid.Col span={{ xs: '100%' }}>
            <GridBuilder props={gridProps} />
          </Grid.Col>
        </Grid>
      </V2FormProvider>
      <SurveyResponseModal
        answers={surveyAnswers}
        sourceSessionId={surveySourceSessionId}
      />
    </Box>
  );
};
